import {
  CONTAINER_GROUND,
  ItemKind,
  WEIGHT_SCALE,
  itemType,
  type InventoryEntry,
  type InventoryPacket,
} from '@game/protocol';

import { hideItemTooltip } from './item-tooltip.ts';
import { collapsiblePanel, type PanelHeader } from './panel.ts';
import {
  clearDropHighlight,
  itemSlot,
  onIconsChanged,
  takeDragOrigin,
  type SlotHandlers,
} from './slot.ts';

interface Node {
  entry: InventoryEntry;
  children: InventoryEntry[];
}

/**
 * Janela de contêineres com arrastar e soltar (PRD §6).
 *
 * O servidor manda uma lista plana com `parentId`; esta classe remonta a hierarquia e desenha
 * mochila, bolsa e o que mais estiver aberto. O que ela **não** faz é decidir se um movimento
 * é válido: ela pede, o servidor responde com um inventário novo. Prever a resposta aqui
 * criaria dois lugares para a mesma regra de peso e capacidade — e o cliente estaria errado
 * toda vez que a regra mudasse.
 *
 * O que o personagem veste saiu daqui e virou painel próprio (ver `equipment.ts`): mochila é
 * lista, corpo é retrato, e as duas empilhadas na mesma grade de cinco colunas se liam como
 * uma coisa só. Os dois painéis continuam recebendo o mesmo `InventoryPacket`, porque vestir
 * uma peça muda os dois no mesmo instante.
 */
export class InventoryWindow {
  private readonly root: HTMLElement;
  private readonly handlers: SlotHandlers;
  private header: PanelHeader | null = null;

  private entries: InventoryEntry[] = [];

  constructor(root: HTMLElement, handlers: SlotHandlers) {
    this.root = root;
    this.handlers = handlers;
    this.root.classList.add('inventory');

    // O resolvedor de ícone chega depois da montagem da interface; sem este registro, a
    // mochila cheia nasceria como uma parede de texto até o primeiro item movido.
    onIconsChanged(() => this.render());
    this.render();
  }

  toggle(): void {
    this.header?.toggle();
  }

  update(packet: InventoryPacket): void {
    this.entries = packet.entries;
    this.render();
  }

  // --- Render --------------------------------------------------------------

  private render(): void {
    // O `innerHTML = ''` logo abaixo mata o slot sob o cursor sem disparar `mouseleave`,
    // e a dica dele ficaria órfã na tela — o inventário chega a cada item movido. O realce
    // de destino do arraste é solto pelo mesmo motivo: o elemento aceso sai do documento, e
    // a variável que aponta para ele passaria a guardar um nó morto.
    hideItemTooltip();
    clearDropHighlight();
    this.root.innerHTML = '';

    this.header = collapsiblePanel(
      this.root,
      'inventario',
      `Inventário · ${this.totalWeightLabel()}`,
    );
    this.root.append(this.header.root);

    // Um bloco por contêiner carregado, em qualquer profundidade: a mochila e a bolsa
    // dentro dela aparecem lado a lado, o que evita navegação em árvore para pegar algo.
    for (const container of this.containers()) {
      this.root.append(this.containerBlock(container));
    }

    this.root.append(this.groundZone());
  }

  private containers(): Node[] {
    const result: Node[] = [];

    for (const entry of this.entries) {
      const type = itemType(entry.typeId);
      if (type?.kind !== ItemKind.Container) continue;

      result.push({
        entry,
        children: this.entries.filter((child) => child.parentId === entry.id),
      });
    }

    return result;
  }

  private containerBlock(node: Node): HTMLElement {
    const block = document.createElement('div');
    block.className = 'container-block';

    const title = document.createElement('div');
    title.className = 'container-title';
    title.textContent = itemType(node.entry.typeId)?.name ?? 'Contêiner';
    block.append(title);

    const grid = document.createElement('div');
    grid.className = 'container-grid';

    const capacity = itemType(node.entry.typeId)?.capacity ?? 0;
    for (let slot = 0; slot < capacity; slot++) {
      const child = node.children.find((candidate) => candidate.slot === slot);
      grid.append(itemSlot(this.handlers, node.entry.id, slot, child, ''));
    }

    block.append(grid);
    return block;
  }

  /** Zona de descarte: soltar aqui larga o item no chão do tile em que se está. */
  private groundZone(): HTMLElement {
    const zone = document.createElement('div');
    zone.className = 'ground-zone';
    zone.textContent = 'soltar no chão';

    zone.addEventListener('dragover', (event) => {
      event.preventDefault();
      zone.classList.add('drop-target');
    });
    zone.addEventListener('dragleave', () => zone.classList.remove('drop-target'));

    zone.addEventListener('drop', (event) => {
      event.preventDefault();
      zone.classList.remove('drop-target');

      // A origem sai do módulo dos slots, e não de um campo desta janela: o item largado
      // aqui pode ter vindo do corpo, que é outro painel (ver `slot.ts`).
      const source = takeDragOrigin();
      if (!source) return;

      this.handlers.onMove(source.container, source.slot, CONTAINER_GROUND, 0, 0);
    });

    return zone;
  }

  /**
   * O peso de tudo que o personagem carrega — corpo e mochila juntos.
   *
   * Continua contando os slots de equipamento mesmo depois de eles terem saído desta janela,
   * e é de propósito: a pergunta que o número responde é "quanto estou carregando", e a
   * armadura vestida pesa no mesmo braço que a mochila. Contar só o que está à vista faria a
   * conta cair sozinha ao vestir uma peça.
   */
  private totalWeightLabel(): string {
    let total = 0;
    for (const entry of this.entries) {
      total += (itemType(entry.typeId)?.weight ?? 0) * entry.count;
    }
    return formatWeight(total);
  }
}

function formatWeight(units: number): string {
  return `${(units / WEIGHT_SCALE).toFixed(1)} oz`;
}
